import React, { useState, useEffect } from "react";
import { Button, Row, Col, Skeleton, Card, Input } from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { message } from "antd";
import Swal from "sweetalert2";
import ButtonBar from "../../components/ButtonNavigation";
import { reorderPhotos, reorderSections, updateReportShareSettings } from "../../services/reportsService";
import usePhotoReport from "../../hooks/usePhotoReport";
import ReportHeader from "./reports/ReportHeader";
import ReportSidebar from "./reports/ReportSidebar";
import PhotoSection from "./reports/PhotoSection";
import SettingsModal from "./reports/modals/SettingsModal";
import AddPhotoModal from "./reports/modals/AddPhotoModal";
import RearrangeModal from "./reports/modals/RearrangeModal";
import ShareReportModal from "./reports/modals/ShareReportModal";
import SunEditor from "suneditor-react";
import {
  align,
  font,
  fontColor,
  fontSize,
  formatBlock,
  hiliteColor,
  horizontalRule,
  lineHeight,
  list,
  paragraphStyle,
  table,
  textStyle,
  link
} from "suneditor/src/plugins";
import "./ProjectPhotoReportDetail.css";

const sunEditorOpts = {
  showPathLabel: false,
  height: "auto",
  minHeight: "180px",
  plugins: [
    align,
    font,
    fontColor,
    fontSize,
    formatBlock,
    hiliteColor,
    horizontalRule,
    lineHeight,
    list,
    paragraphStyle,
    table,
    textStyle,
    link
  ],
  buttonList: [
    ["undo", "redo"],
    ["font", "fontSize", "formatBlock"],
    ["bold", "underline", "italic", "strike"],
    ["fontColor", "hiliteColor"],
    ["outdent", "indent"],
    ["align", "horizontalRule", "list", "lineHeight"],
    ["table", "link"],
    ["fullScreen", "codeView"]
  ]
};

const ProjectPhotoReportDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Use the custom hook for all data and state management
  const {
    report,
    sections,
    setSections,
    projectData,
    projectImages,
    layoutOptions,
    editTitle,
    setEditTitle,
    editLayout,
    setEditLayout,
    claimNumber,
    setClaimNumber,
    showConclusion,
    setShowConclusion,
    conclusion,
    setConclusion,
    coverSettings,
    setCoverSettings,
    activeSectionIndex,
    setActiveSectionIndex,
    loading,
    generatingPDF,
    savingSettings,
    addingSection,
    savingConclusion,
    savingSectionTitle,
    savingSectionSummary,
    savingPhotoDescId,
    actions,
    loadSections
  } = usePhotoReport();

  // Modal state
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [addPhotoModalVisible, setAddPhotoModalVisible] = useState(false);
  const [rearrangeModalVisible, setRearrangeModalVisible] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [insertPhotoIndex, setInsertPhotoIndex] = useState(null);
  const [rearrangeSectionIndex, setRearrangeSectionIndex] = useState(null);
  const [rearrangePhotos, setRearrangePhotos] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [addingPhotos, setAddingPhotos] = useState(false);
  const [editingConclusion, setEditingConclusion] = useState(false);
  const [tempConclusion, setTempConclusion] = useState('');
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const [deletingReport, setDeletingReport] = useState(false);
  const [deletingSectionIndex, setDeletingSectionIndex] = useState(null);
  const [shareToken, setShareToken] = useState('');
  const [syncingToken, setSyncingToken] = useState(false);

  // Function to generate a random token for shared links
  const generateShareToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Initial token sync from report data or generate new if missing
  useEffect(() => {
    const initShareToken = async () => {
      if (report?.share_token) {
        setShareToken(report.share_token);
        setSyncingToken(false);
        return;
      }
      if (shareToken || !report?.id) return;
      const newToken = generateShareToken();
      setShareToken(newToken);
      setSyncingToken(true);
      try {
        await dispatch(updateReportShareSettings(report.id, {
          share_token: newToken,
          share_token_expires_at: null 
        }));
      } catch (err) {
        console.error("Failed to auto-save new share token:", err);
        message.error("Link sync failed. Please try 'Generate New Link'.");
      } finally {
        setSyncingToken(false);
      }
    };
    if (report && !loading) {
      initShareToken();
    }
  }, [report, loading, dispatch]);

  // Handlers
  const handleOpenAddPhotoModal = (sectionIndex, photoIndex = null) => {
    setCurrentSectionIndex(sectionIndex);
    setInsertPhotoIndex(photoIndex);
    setSelectedImages([]);
    setAddPhotoModalVisible(true);
  };

  const handleAddPhotosToSection = async () => {
    if (selectedImages.length === 0) {
      message.warning('Please select at least one photo');
      return;
    }

    setAddingPhotos(true);
    try {
      await actions.addPhotosToSection(currentSectionIndex, selectedImages, insertPhotoIndex);
      setAddPhotoModalVisible(false);
      setSelectedImages([]);
      setInsertPhotoIndex(null);
    } catch (error) {
      // Error already handled in the hook
    } finally {
      setAddingPhotos(false);
    }
  };

  const handleOpenRearrangeModal = (sectionIndex) => {
    setRearrangeSectionIndex(sectionIndex);
    setRearrangePhotos([...sections[sectionIndex].photos]);
    setRearrangeModalVisible(true);
  };

  const handleSaveRearrangedPhotos = (reorderedPhotos) => {
    setSavingOrder(true);
    const section = sections[rearrangeSectionIndex];
    const photoIds = reorderedPhotos.map(p => p.id);

    dispatch(reorderPhotos(section.id, photoIds))
      .then(() => {
        loadSections();
        setRearrangeModalVisible(false);
        message.success('Photos rearranged successfully');
      })
      .catch((error) => {
        console.error('Failed to reorder photos:', error);
        message.error('Failed to reorder photos');
      })
      .finally(() => {
        setSavingOrder(false);
      });
  };

  const handleMovePhotoInModal = (dragIndex, hoverIndex) => {
    const dragPhoto = rearrangePhotos[dragIndex];
    const newPhotos = [...rearrangePhotos];
    newPhotos.splice(dragIndex, 1);
    newPhotos.splice(hoverIndex, 0, dragPhoto);
    setRearrangePhotos(newPhotos);
  };

  const handleDeleteSection = (sectionIndex) => {
    Swal.fire({
      title: 'Delete Section?',
      text: 'Are you sure you want to delete this section? All photos in this section will be removed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setDeletingSectionIndex(sectionIndex);
        actions.deleteSection(sectionIndex)
          .finally(() => {
            setDeletingSectionIndex(null);
          });
      }
    });
  };

  const handleDeletePhoto = async (sectionIndex, photoId) => {
    const result = await Swal.fire({
      title: 'Delete Photo?',
      text: 'Are you sure you want to remove this photo from the section?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      setDeletingPhotoId(photoId);
      try {
        await actions.deletePhotoFromSection(sectionIndex, photoId);
      } finally {
        setDeletingPhotoId(null);
      }
    }
  };

  const handleDeleteReport = async () => {
    const result = await Swal.fire({
      title: 'Delete Report?',
      text: 'Are you sure you want to delete this report? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      setDeletingReport(true);
      try {
        await actions.deleteReport();
      } finally {
        setDeletingReport(false);
      }
    }
  };

  const handleReorderSections = (oldIndex, newIndex) => {
    const newSections = arrayMove(sections, oldIndex, newIndex);
    const sectionIds = newSections.map(s => s.id);
    
    // Optimistic update - immediately update local state
    setSections(newSections);
    
    // Backend call
    dispatch(reorderSections(report.id, sectionIds))
      .then(() => {
        message.success('Sections reordered successfully');
      })
      .catch((error) => {
        console.error('Failed to reorder sections:', error);
        message.error('Failed to reorder sections');
        // Revert on error
        loadSections();
      });
  };

  const arrayMove = (array, from, to) => {
    const newArray = [...array];
    const item = newArray.splice(from, 1)[0];
    newArray.splice(to, 0, item);
    return newArray;
  };

  if (loading || !report) {
    return (
      <div className="p-6">
        <div className="container-xxl">
          <div className="button-navigations mt-5">
            <ButtonBar />
          </div>
          <div className="project-detail-section mt-4">
            {/* Back Button */}
            <div className="row mb-4">
              <div className="col-12">
                <Skeleton.Button active style={{ width: 150 }} />
              </div>
            </div>

            {/* Header - 78px */}
            <div style={{ background: 'white', padding: '20px 24px', borderRadius: '8px', marginBottom: '24px', minHeight: '78px' }}>
              <Row gutter={24} align="middle">
                <Col xs={24} md={8}>
                  <Skeleton.Input active style={{ width: 200 }} />
                </Col>
                <Col xs={24} md={16} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <Skeleton.Button active style={{ width: '160px', borderRadius: '6px' }} />
                  <Skeleton.Button active style={{ width: '160px', borderRadius: '6px' }} />
                  <Skeleton.Button active style={{ width: '160px', borderRadius: '6px' }} />
                </Col>
              </Row>
            </div>

            {/* Sections Layout */}
            <Row gutter={24}>
              {/* Left Sidebar - Col md={6} */}
              <Col xs={24} md={6}>
                <div style={{ background: 'white', padding: '16px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                  <Skeleton.Input active style={{ width: '100%', marginBottom: '12px' }} />
                  <Skeleton.Button active block style={{ marginBottom: '8px', borderRadius: '4px' }} />
                  <Skeleton.Button active block style={{ borderRadius: '16px', marginTop: '16px' }} />
                </div>
              </Col>

              {/* Right Content - Col md={18} */}
              <Col xs={24} md={18}>
                {/* Section Header */}
                <div style={{ background: 'white', padding: '16px 24px', marginBottom: '16px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton.Input active style={{ width: 150 }} />
                  <Skeleton.Avatar active size="large" />
                </div>

                {/* Section Card */}
                <Card style={{ marginBottom: '24px' }}>
                  <Skeleton.Input active style={{ width: 100, marginBottom: '16px' }} />

                  {/* Photo Items */}
                  {[1, 2].map((item) => (
                    <div key={item} style={{ marginBottom: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px' }}>
                      <Row gutter={8}>
                        <Col span={8}>
                          <Skeleton.Image active style={{ width: '300px', height: '300px' }} />
                        </Col>
                        <Col span={16}>
                          <Skeleton active paragraph={{ rows: 4 }} />
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <Skeleton active paragraph={{ rows: 2 }} style={{ marginTop: '16px' }} />
                  <Skeleton.Button active style={{ width: '200px', borderRadius: '16px', marginTop: '16px' }} />
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="container-xxl">
        <div className="button-navigations mt-5">
          <ButtonBar />
        </div>

        <div className="project-detail-section mt-4">
          <div className="row mb-4">
            <div className="col-12">
              <Button
                className="button-back"
                onClick={() => {
                  const projectId = report?.project_id || report?.projectId;
                  if (projectId) {
                    navigate(`/project-detail/?project_id=${projectId}`);
                  } else {
                    message.error('Project ID not found');
                    navigate('/projects');
                  }
                }}
              >
                <ArrowLeftOutlined /> Back to Project
              </Button>
            </div>
          </div>

          {/* Report Header */}
          <ReportHeader
            title={report.title}
            onGeneratePDF={actions.generatePDF}
            generatingPDF={generatingPDF}
            onOpenSettings={() => setSettingsModalVisible(true)}
            onDeleteReport={handleDeleteReport}
            deletingReport={deletingReport}
            onShareReport={() => setShareModalVisible(true)}
          />

          {/* Sections */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Left Sidebar */}
            <Col xs={24} md={6} className="left-section-card">
              <ReportSidebar
                sections={sections}
                activeSectionIndex={activeSectionIndex}
                onSectionClick={setActiveSectionIndex}
                onAddSection={actions.addSection}
                addingSection={addingSection}
                showConclusion={showConclusion}
                onReorderSections={handleReorderSections}
                deletingSectionIndex={deletingSectionIndex}
              />
            </Col>

            {/* Right Section Cards */}
            <Col xs={24} md={18} className="right-section-card">
              {addingSection ? (
                <>
                  {/* Header Skeleton - 70px */}
                  <div style={{ background: 'white', padding: '16px 24px', marginBottom: '16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '70px', display: 'flex', alignItems: 'center' }}>
                    <Skeleton.Input active style={{ width: 200, height: 24 }} />
                  </div>

                  {/* Card with Content */}
                  <Card style={{ marginBottom: '24px' }}>
                    <Skeleton.Input active style={{ width: 150, height: 20, marginBottom: '20px' }} />
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: '8px', padding: '12px', height: '80px', marginBottom: '16px' }}>
                      <Skeleton.Input active style={{ width: 150, height: 16, marginBottom: '8px' }} />
                    </div>
                    <Skeleton.Button active style={{ width: 180, height: 40, marginTop: '16px', borderRadius: '16px' }} />
                  </Card>
                </>
              ) : activeSectionIndex === sections.length && showConclusion ? (
                <Card>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0 }}>Conclusion</h3>
                    {!editingConclusion && (
                      <Button
                        type="default"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditingConclusion(true);
                          setTempConclusion(conclusion || '');
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                  {editingConclusion ? (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <SunEditor
                          setContents={tempConclusion}
                          onChange={(c) => setTempConclusion(c)}
                          setOptions={sunEditorOpts}
                          placeholder="Add conclusion..."
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          type="primary"
                          className="btn-GeneratePDF"
                          size="small"
                          onClick={async () => {
                            await actions.updateConclusion(tempConclusion);
                            setEditingConclusion(false);
                          }}
                          loading={savingConclusion}
                          style={{ fontSize: '13px' }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingConclusion(false);
                            setTempConclusion(conclusion || '');
                          }}
                          style={{ fontSize: '13px' }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div
                      onClick={() => {
                        setEditingConclusion(true);
                        setTempConclusion(conclusion || '');
                      }}
                      style={{
                        cursor: 'pointer',
                        padding: '12px 16px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        minHeight: '120px',
                        backgroundColor: '#fafafa',
                        lineHeight: 1.5,
                        wordBreak: 'break-word'
                      }}
                    >
                      {conclusion && conclusion.trim() !== '' && conclusion !== '<p><br></p>' ? (
                        <div
                          className="photoreport-rich-content"
                          dangerouslySetInnerHTML={{ __html: conclusion }}
                        />
                      ) : (
                        <span style={{ color: '#8c8c8c' }}>Click to add conclusion...</span>
                      )}
                    </div>
                  )}
                </Card>
              ) : (
                sections.map((section, sectionIndex) =>
                  activeSectionIndex === sectionIndex && (
                    <PhotoSection
                      key={section.id}
                      section={section}
                      sectionIndex={sectionIndex}
                      projectData={projectData}
                      coverSettings={coverSettings}
                      onUpdateTitle={actions.updateSectionTitle}
                      onUpdateSummary={actions.updateSectionSummary}
                      onDeleteSection={handleDeleteSection}
                      onRearrangePhotos={handleOpenRearrangeModal}
                      onUpdateCoverPhoto={actions.updateCoverPhoto}
                      onDeletePhoto={handleDeletePhoto}
                      onUpdateDescription={actions.updatePhotoDescription}
                      onAddPhotos={() => handleOpenAddPhotoModal(sectionIndex)}
                      onInsertPhoto={handleOpenAddPhotoModal}
                      deletingPhotoId={deletingPhotoId}
                      savingSectionTitle={savingSectionTitle}
                      savingSectionSummary={savingSectionSummary}
                      savingPhotoDescId={savingPhotoDescId}
                    />
                  )
                )
              )}
            </Col>
          </Row>
        </div>

        {/* Settings Modal */}
        <SettingsModal
          visible={settingsModalVisible}
          onCancel={() => setSettingsModalVisible(false)}
          onSave={actions.saveReportSettings}
          saving={savingSettings}
          report={report}
          editTitle={editTitle}
          setEditTitle={setEditTitle}
          editLayout={editLayout}
          setEditLayout={setEditLayout}
          claimNumber={claimNumber}
          setClaimNumber={setClaimNumber}
          showConclusion={showConclusion}
          setShowConclusion={setShowConclusion}
          conclusion={conclusion}
          setConclusion={setConclusion}
          coverSettings={coverSettings}
          setCoverSettings={setCoverSettings}
          layoutOptions={layoutOptions}
        />

        {/* Add Photos Modal */}
        <AddPhotoModal
          visible={addPhotoModalVisible}
          onCancel={() => {
            setAddPhotoModalVisible(false);
            setSelectedImages([]);
            setInsertPhotoIndex(null);
          }}
          onAdd={handleAddPhotosToSection}
          adding={addingPhotos}
          projectImages={projectImages}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
        />

        {/* Rearrange Photos Modal */}
        <RearrangeModal
          visible={rearrangeModalVisible}
          onCancel={() => setRearrangeModalVisible(false)}
          onSave={handleSaveRearrangedPhotos}
          saving={savingOrder}
          sectionTitle={rearrangeSectionIndex !== null ? sections[rearrangeSectionIndex]?.title : ''}
          photos={rearrangePhotos}
          onMovePhoto={handleMovePhotoInModal}
        />

        {/* Share Report Modal */}
        <ShareReportModal
          visible={shareModalVisible}
          onCancel={() => setShareModalVisible(false)}
          shareLink={`${window.location.origin}/shared-report/${shareToken}`}
          isSyncing={syncingToken}
          onGenerateNewLink={async () => {
            const newToken = generateShareToken();
            setSyncingToken(true);
            try {
              await dispatch(updateReportShareSettings(report.id, {
                share_token: newToken,
                share_token_expires_at: null 
              }));
              setShareToken(newToken);
              message.success("New secure link generated and saved!");
            } catch (error) {
              message.error("Failed to update share link on server.");
            } finally {
              setSyncingToken(false);
            }
          }}
          onViewReport={() => {
            window.open(`/shared-report/${shareToken}`, '_blank');
          }}
          reportTitle={report?.title}
        />
      </div>
    </div>
  );
};

export default ProjectPhotoReportDetail;
